import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Loader2, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusStrip } from '@/features/settings/StatusStrip';
import { dateLocaleFor } from '@/i18n';
import { deviceLabel } from './userAgent';
import { useCloseSession, useSessions, type Session, type SessionScope } from './api';

interface SessionListProps {
  scope: SessionScope;
  /** Called after the caller's *current* session is revoked, so the host can drop to login. */
  onAllClosed?: () => void;
}

export function SessionList({ scope, onAllClosed }: SessionListProps) {
  const { t } = useTranslation();
  const formatTime = useFormattedTime();
  const sessions = useSessions(scope);

  if (sessions.isPending) {
    return (
      <StatusStrip>
        <span className="text-sm text-muted-foreground">{t('sessions.loading')}</span>
        <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
      </StatusStrip>
    );
  }
  if (sessions.isError) {
    return (
      <StatusStrip>
        <span className="text-sm text-destructive">{t('sessions.load_error')}</span>
      </StatusStrip>
    );
  }

  const list = sessions.data;
  if (list.length === 0) {
    return (
      <StatusStrip>
        <span className="text-sm text-muted-foreground">{t('sessions.empty')}</span>
      </StatusStrip>
    );
  }

  return (
    <ul className="divide-y rounded-md border" aria-label={t('sessions.title')}>
      {list.map((session) => (
        <li key={session.id} className="flex items-start justify-between gap-3 p-3">
          <div className="min-w-0 space-y-1 leading-tight">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {deviceLabel(session.userAgent) ?? t('sessions.unknown_device')}
              </span>
              {session.current ? (
                <Badge variant="secondary">{t('sessions.this_device')}</Badge>
              ) : null}
            </div>
            <div className="text-xs text-muted-foreground">
              {session.ip ?? '—'}
              {' · '}
              {t('sessions.last_active')}: {formatTime(session.lastSeenAt)}
            </div>
          </div>
          <RowCloseButton scope={scope} session={session} onAllClosed={onAllClosed} />
        </li>
      ))}
    </ul>
  );
}

function useFormattedTime() {
  const { i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return (iso: string) => format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale: dateLocaleFor(lng) });
}

function RowCloseButton({
  scope,
  session,
  onAllClosed,
}: {
  scope: SessionScope;
  session: Session;
  onAllClosed?: () => void;
}) {
  const { t } = useTranslation();
  const close = useCloseSession(scope);

  const onConfirm = () => {
    close.mutate(session.id, {
      onSuccess: () => {
        toast.success(t('sessions.toasts.closed'));
        if (session.current) onAllClosed?.();
      },
      onError: () => toast.error(t('sessions.toasts.close_failed')),
    });
  };

  return (
    <ConfirmDialog
      title={session.current ? t('sessions.confirm.close_current_title') : t('sessions.confirm.close_title')}
      description={
        session.current
          ? t('sessions.confirm.close_current_description')
          : t('sessions.confirm.close_description')
      }
      confirmLabel={t('sessions.close')}
      destructive
      onConfirm={onConfirm}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('sessions.close')}
        disabled={close.isPending}
      >
        <Trash2 />
      </Button>
    </ConfirmDialog>
  );
}
